import React, { useEffect, useState } from "react";
import {
  IonCard,
  IonCol,
  IonGrid,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTextarea,
} from "@ionic/react";
import { ICart } from "../../models";
import { OrderPriceDetails } from "./OrderPriceDetails";
import { capitalizeFirstLetter } from "../../common/utils";
import { useCommon } from "../../common/useCommon";
import { useCart } from "./useCart";
import { FrameAssembledBihWarning } from "../../components/FrameAssembledBihWarning/FrameAssembledBihWarning";
import { BihPriceWarning } from "../../components/BihPriceWarning/BihPriceWarning";
import {
  BIH_MAX_ORDER_PRICE_KM,
  BIH_PRICE_LIMIT_MESSAGE,
} from "../../common/constants";
import { convertPrice } from "../../common/currencyUtils";
import { OrderErrors, OrderField, orderFieldId } from "./orderValidation";

interface Props {
  cart: ICart;
  updateCart: (cart: ICart) => void;
  /** Card payment needs an email: the payment confirmation is sent there. */
  emailRequired?: boolean;
  /** Payment method picker, rendered below the address form. */
  paymentSection?: React.ReactNode;
  /** Problems with the form (orderValidation). */
  errors: OrderErrors;
  /** Show every error, not only those of fields the customer has left: after a click on NARUČI. */
  showAllErrors: boolean;
}

/* One form field: label (with * when required), input, and the error under it once shown. */
const Field: React.FC<{
  field: OrderField;
  label: React.ReactNode;
  required?: boolean;
  error?: string;
}> = ({ field, label, required, error, children }) => (
  <IonItem
    id={orderFieldId(field)}
    style={
      error
        ? ({ "--border-color": "var(--ion-color-danger)" } as React.CSSProperties)
        : undefined
    }
  >
    {/* Colour by style, not by color=: a legacy IonItem copies the label colour onto its focus
        underline and does not reliably reset it when the field becomes valid. */}
    <IonLabel
      color="medium"
      position="stacked"
      className={required ? "required" : ""}
      style={error ? { color: "var(--ion-color-danger)" } : undefined}
    >
      {label}
    </IonLabel>
    {children}
    {error && (
      <IonText color="danger" style={{ fontSize: 13, paddingBottom: 8 }}>
        {error}
      </IonText>
    )}
  </IonItem>
);

export const CartOrder: React.FC<Props> = ({
  cart,
  updateCart,
  emailRequired,
  paymentSection,
  errors,
  showAllErrors,
}) => {
  const { currency, setCurrency, conversionRate } = useCommon();
  const {
    framesAssembled,
    removeAllAssembled,
    convertAssembledToFrames,
    priceTotal,
  } = useCart();

  const [showAssembledWarning, setShowAssembledWarning] =
    useState<boolean>(false);
  const [showPriceWarning, setShowPriceWarning] = useState<boolean>(false);

  // A field is marked only once the customer has left it, or after a click on NARUČI,
  // so an empty form does not start out full of errors.
  const [touched, setTouched] = useState<Partial<Record<OrderField, boolean>>>({});
  const touch = (field: OrderField) =>
    setTouched((t) => (t[field] ? t : { ...t, [field]: true }));
  const errorOf = (field: OrderField) =>
    showAllErrors || touched[field] ? errors[field] : undefined;

  const priceTotalKM =
    conversionRate > 0 ? convertPrice(priceTotal(), "KM", conversionRate) : 0;
  const formattedPriceKM = priceTotalKM.toFixed(2).replace(".", ",") + " KM";
  const isPriceOverLimit = priceTotalKM > BIH_MAX_ORDER_PRICE_KM;

  const country = currency === "KM" ? "BIH" : "SRB";
  const phonePrefix = country === "BIH" ? "+387" : "";

  useEffect(() => {
    if (!cart.phone || cart.phone === "" || cart.phone === "+387") {
      updateCart({ ...cart, phone: phonePrefix, country });
    } else {
      updateCart({ ...cart, country });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phonePrefix]);

  const onCountryChange = (value: string) => {
    if (value === "BIH") {
      if (framesAssembled > 0) {
        setShowAssembledWarning(true);
      } else if (isPriceOverLimit) {
        setCurrency("KM");
        setShowPriceWarning(true);
      } else {
        setCurrency("KM");
      }
    } else {
      setCurrency("DIN");
    }
  };

  return (
    <>
      <IonGrid style={{ margin: 0, padding: 0 }}>
        <IonRow>
          <IonCol
            size="12"
            sizeMd="6"
            offsetMd="3"
            style={{ margin: 0, padding: 0 }}
          >
            <OrderPriceDetails deliveryInfo />

            <IonCard>
              <IonList>
                <IonItem>
                  <IonLabel color="medium" position="stacked">
                    Država
                  </IonLabel>
                  <IonSelect
                    value={country}
                    onIonChange={(e) => onCountryChange(e.detail.value)}
                    interface="popover"
                  >
                    <IonSelectOption value="SRB">Srbija</IonSelectOption>
                    <IonSelectOption value="BIH" disabled={conversionRate <= 0}>
                      Bosna i Hercegovina
                    </IonSelectOption>
                  </IonSelect>
                </IonItem>
                <Field field="name" label="Ime i prezime" required error={errorOf("name")}>
                  <IonInput
                    onIonBlur={() => touch("name")}
                    type="text"
                    // name="name"
                    // autocomplete="name"
                    required
                    value={cart.name}
                    onIonInput={(e: any) =>
                      updateCart({
                        ...cart,
                        name: capitalizeFirstLetter(e.detail.value),
                      })
                    }
                  />
                </Field>
                <Field field="address" label="Adresa" required error={errorOf("address")}>
                  <IonInput
                    onIonBlur={() => touch("address")}
                    type="text"
                    // name="street-address"
                    // autocomplete="street-address"
                    required
                    value={cart.address}
                    onIonInput={(e: any) =>
                      updateCart({
                        ...cart,
                        address: capitalizeFirstLetter(e.detail.value),
                      })
                    }
                  />
                </Field>
                <Field field="zip" label="Poštanski broj" required error={errorOf("zip")}>
                  <IonInput
                    onIonBlur={() => touch("zip")}
                    type="text"
                    // name="postal-code"
                    // autocomplete="postal-code"
                    required
                    value={cart.zip}
                    onIonInput={(e: any) =>
                      updateCart({ ...cart, zip: e.detail.value })
                    }
                  />
                </Field>
                <Field field="place" label="Mesto" required error={errorOf("place")}>
                  <IonInput
                    onIonBlur={() => touch("place")}
                    type="text"
                    // name="city"
                    // autocomplete="address-level2"
                    required
                    value={cart.place}
                    onIonInput={(e: any) =>
                      updateCart({
                        ...cart,
                        place: capitalizeFirstLetter(e.detail.value),
                      })
                    }
                  />
                </Field>
                <Field field="phone" label="Kontakt telefon" required error={errorOf("phone")}>
                  <IonInput
                    type="text"
                    // name="tel"
                    // autocomplete="tel"
                    placeholder={country === "BIH" ? "+387" : ""}
                    required={!!cart.phone && cart.phone.length <= 6}
                    value={cart.phone}
                    onIonInput={(e: any) => {
                      let val: string = e.detail.value ?? "";
                      // Remove all chars that are not digits, +, -, or /
                      val = val.replace(/[^0-9+\-/]/g, "");
                      // + only allowed as first character
                      val = val.replace(/(?!^)\+/g, "");
                      // - and / not allowed as first character
                      if (val.startsWith("-") || val.startsWith("/")) val = val.slice(1);
                      updateCart({ ...cart, phone: val });
                    }}
                    onIonBlur={() => {
                      touch("phone");
                      let val: string = cart.phone ?? "";
                      // - and / not allowed as last character
                      while (val.endsWith("-") || val.endsWith("/")) val = val.slice(0, -1);
                      updateCart({ ...cart, phone: val });
                    }}
                  />
                </Field>
                <Field
                  field="email"
                  label={emailRequired ? "Email (za potvrdu o plaćanju)" : "Email"}
                  required={emailRequired}
                  error={errorOf("email")}
                >
                  <IonInput
                    onIonBlur={() => touch("email")}
                    type="text"
                    // name="email"
                    // autocomplete="email"
                    value={cart.email}
                    onIonInput={(e: any) =>
                      updateCart({ ...cart, email: e.detail.value })
                    }
                  />
                </Field>
                <IonItem>
                  <IonLabel color="medium" position="stacked">
                    Napomena
                  </IonLabel>
                  <IonTextarea
                    rows={3}
                    value={cart.note}
                    onIonInput={(e: any) =>
                      updateCart({ ...cart, note: e.detail.value })
                    }
                  />
                </IonItem>
              </IonList>
            </IonCard>
            {paymentSection}
          </IonCol>
        </IonRow>
      </IonGrid>
      <FrameAssembledBihWarning
        isOpen={showAssembledWarning}
        framesAssembled={framesAssembled}
        priceWarningMessage={
          isPriceOverLimit
            ? BIH_PRICE_LIMIT_MESSAGE(formattedPriceKM)
            : undefined
        }
        onDismiss={() => setShowAssembledWarning(false)}
        onRemove={() => {
          removeAllAssembled();
          setCurrency("KM");
          setShowAssembledWarning(false);
        }}
        onConvert={() => {
          convertAssembledToFrames();
          setCurrency("KM");
          setShowAssembledWarning(false);
        }}
      />
      <BihPriceWarning
        isOpen={showPriceWarning}
        formattedPriceKM={formattedPriceKM}
        priceTotalKM={priceTotalKM}
        onDismiss={() => setShowPriceWarning(false)}
      />
    </>
  );
};
